import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  AddCircleOutline,
  RemoveCircleOutline,
  Save,
  LocalDrink,
  Fastfood,
  Whatshot,
  DirectionsWalk,
} from "@mui/icons-material";
import {
  getDecryptedAccessToken,
  getDecryptedData,
  getDecryptedUserType,
} from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import ApiServices from "../../../Utils/ApiServices/ApiServices";

const DailyGoalData = ({ patientData }) => {
  const LoginUserDetails = getDecryptedData();
  const LoginUserType = getDecryptedUserType();
  const accessToken = getDecryptedAccessToken();
  const pid = LoginUserType === "Patient" ? LoginUserDetails?.pid : patientData?.pid;

  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const [values, setValues] = useState({
    waterIntake: 0,
    caloriesIntake: 0,
    caloriesBurn: 0,
    steps: 0,
  });

  useEffect(() => {
    fetchDailyGoals();
  }, []);

  const fetchDailyGoals = async () => {
    try {
      setLoading(true);
      const response = await ApiServices.get(`/apis/default/portal/dailygoalsetup/${pid}`, {
        Authorization: `Bearer ${accessToken}`,
      });

      const data = response.data.data || {};
      console.log("GET DAILY LOG DATA", data);

      setHasData(!!data);

      setValues({
        waterIntake: Math.round(data.water_intake_liters) || 0,
        caloriesIntake: Math.round(data.calories_intake) || 0,
        caloriesBurn: Math.round(data.calories_burn) || 0,
        steps: Math.round(data.steps) || 0,
      });
    } catch (err) {
      console.error("API Error:", err);
      setSnackbar({ open: true, message: "Failed to fetch daily goals.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const updateValue = (field, delta) => {
    setValues((prev) => ({
      ...prev,
      [field]: Math.max(0, Math.round(prev[field] + delta)), // Ensure values don't go negative
    }));
  };

  const handleInputChange = (field, value) => {
    const newValue = value.replace(/[^0-9]/g, ""); // Allow only numbers
    setValues((prev) => ({ ...prev, [field]: newValue === "" ? 0 : parseInt(newValue, 10) }));
  };

  const handleSubmit = async () => {
    try {
      if (!pid) throw new Error("User ID is missing.");

      const payload = {
        water_intake_liters: parseFloat(values.waterIntake), // Convert to float
        calories_intake: values.caloriesIntake,
        calories_burn: values.caloriesBurn,
        steps: values.steps,
        [hasData ? "updated_by" : "created_by"]: pid,
      };

      const apiUrl = `/apis/default/portal/dailygoalsetup/${pid}`;
      const apiMethod = hasData ? ApiServices.put : ApiServices.post;
      console.log("Payload:", payload);

      const response = await apiMethod(apiUrl, payload, {
        Authorization: `Bearer ${accessToken}`,
      });

      console.log("PUT RESPONSE", response);
      setSnackbar({ open: true, message: "Daily goals updated successfully!", severity: "success" });

      fetchDailyGoals(); // Refresh data
    } catch (err) {
      console.error("API Error:", err);
      setSnackbar({ open: true, message: "Failed to update daily goals.", severity: "error" });
    }
  };

  return (
    <>
       <Typography variant="h5" fontWeight="bold" sx={{ color: "#028391" }}>
       🏆 Daily Goals

          </Typography>
    <Card
      sx={{
        // height: "100%",
        p: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        border: "2px solid #028391",
        borderRadius: "12px",
        minHeight: "260px",
      }}
    >
      {loading ? (
        <CircularProgress sx={{ margin: "20px auto", display: "block", color: "#05b6b3" }} />
      ) : (
        <>
          {[
            { key: "waterIntake", label: "Water Intake (L)", color: "#00BFFF", icon: <LocalDrink /> },
            { key: "caloriesIntake", label: "Calories Intake", color: "orange", icon: <Fastfood /> },
            { key: "caloriesBurn", label: "Calories Burn", color: "red", icon: <Whatshot /> },
            { key: "steps", label: "Steps", color: "green", icon: <DirectionsWalk /> },
          ].map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                // margin: "10px 0",
              }}
            >
              {/* Icon + Label (Left-aligned) */}
              <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                <div style={{ color: item.color, marginRight: "10px" }}>{item.icon}</div>
                <Typography sx={{ fontSize: "16px", fontWeight: "500", color: item.color }}>
                  {item.label}
                </Typography>
              </div>

              {/* Value Controls (Right-aligned) */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <IconButton onClick={() => updateValue(item.key, -1)} sx={{ color: "#FF5733" }}>
                  <RemoveCircleOutline />
                </IconButton>

                <TextField
                  variant="outlined"
                  size="small"
                  sx={{ width: "80px", textAlign: "center" }}
                  inputProps={{ style: { textAlign: "center" } }}
                  value={String(values[item.key])}
                  onChange={(e) => handleInputChange(item.key, e.target.value)}
                />

                <IconButton onClick={() => updateValue(item.key, 1)} sx={{ color: "#4CAF50" }}>
                  <AddCircleOutline />
                </IconButton>
              </div>
            </div>
          ))}

          <Button
            variant="contained"
            startIcon={<Save />}
            sx={{ marginTop: "5px", backgroundColor: "#05b6b3" }}
            onClick={handleSubmit}
          >
            Save Goals
          </Button>
        </>
      )}

      {/* Snackbar for messages */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
    </>
  );
};

export default DailyGoalData;
